import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Check, X, AlertCircle, Search, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

interface Department {
  department_id: string;
  name: string;
  name_tamil?: string;
  registration_code?: string;
}

interface PendingRegistration {
  id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  date_of_birth: string;
  gender: string;
  department_id: string;
  competitions: string[];
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  student_id?: string;
  department?: {
    name: string;
    name_tamil?: string;
  };
}

interface Competition {
  event_id: string;
  event_name: string;
  gender_category: string;
  category?: {
    category_name: string;
  };
  age_group?: {
    group_name: string;
  };
}

const ITEMS_PER_PAGE = 10;

const PendingRegistrationsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [pendingRegistrations, setPendingRegistrations] = useState<PendingRegistration[]>([]);
  const [competitions, setCompetitions] = useState<Record<string, Competition>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      } else {
        loadData();
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch pending registrations
      const { data: registrations, error: registrationsError } = await supabase
        .from('pending_registrations')
        .select(`
          *,
          department:departments(
            name,
            name_tamil
          )
        `)
        .order('created_at', { ascending: false });
      
      if (registrationsError) throw registrationsError;
      
      // Fetch all competitions for reference
      const { data: competitionsData, error: competitionsError } = await supabase
        .from('events')
        .select(`
          event_id,
          event_name,
          category:sports_categories(
            category_name
          ),
          age_group:age_groups(
            group_name
          )
        `);
      
      if (competitionsError) throw competitionsError;
      
      // Convert competitions array to a lookup object
      const competitionsMap = (competitionsData || []).reduce((acc, comp) => {
        acc[comp.event_id] = comp;
        return acc;
      }, {} as Record<string, Competition>);
      
      setPendingRegistrations(registrations || []);
      setCompetitions(competitionsMap);
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (registration: PendingRegistration) => {
    try {
      setProcessingId(registration.id);
      setError(null);
      
      // Check if this is an existing student (student_id is provided)
      if (registration.student_id) {
        // Update the existing student with new information
        const { error: updateError } = await supabase
          .from('students')
          .update({
            first_name: registration.first_name,
            last_name: registration.last_name,
            first_name_tamil: registration.first_name_tamil || null,
            last_name_tamil: registration.last_name_tamil || null,
            date_of_birth: registration.date_of_birth,
            gender: registration.gender
            // Note: We don't update department_id or house_id for existing students
          })
          .eq('student_id', registration.student_id);
          
        if (updateError) throw updateError;
        
        // Register for the selected competitions
        const currentYear = new Date().getFullYear();
        
        // Check for existing registrations to avoid duplicates
        const { data: existingRegistrations, error: checkError } = await supabase
          .from('student_event_registrations')
          .select('event_id')
          .eq('student_id', registration.student_id)
          .eq('competition_year', currentYear);
          
        if (checkError) throw checkError;
        
        // Filter out competitions that are already registered
        const existingEventIds = existingRegistrations?.map(reg => reg.event_id) || [];
        const newCompetitions = registration.competitions.filter(
          competitionId => !existingEventIds.includes(competitionId)
        );
        
        if (newCompetitions.length > 0) {
          const registrations = newCompetitions.map(competitionId => ({
            student_id: registration.student_id!,
            event_id: competitionId,
            competition_year: currentYear,
            status: 'registered'
          }));
          
          const { error: registrationsError } = await supabase
            .from('student_event_registrations')
            .insert(registrations);
          
          if (registrationsError) throw registrationsError;
        }
      } else {
        // This is a new student - create a new record
        // First fetch the default house ID
        const defaultHouseId = await getDefaultHouseId();
        
        // Create the student
        const { data: student, error: studentError } = await supabase
          .from('students')
          .insert([{
            first_name: registration.first_name,
            last_name: registration.last_name,
            first_name_tamil: registration.first_name_tamil || null,
            last_name_tamil: registration.last_name_tamil || null,
            date_of_birth: registration.date_of_birth,
            gender: registration.gender,
            department_id: registration.department_id,
            house_id: defaultHouseId
          }])
          .select()
          .single();
        
        if (studentError) throw studentError;
        
        // Register for competitions
        const currentYear = new Date().getFullYear();
        
        const registrations = registration.competitions.map(competitionId => ({
          student_id: student.student_id,
          event_id: competitionId,
          competition_year: currentYear,
          status: 'registered'
        }));
        
        const { error: registrationsError } = await supabase
          .from('student_event_registrations')
          .insert(registrations);
        
        if (registrationsError) throw registrationsError;
      }
      
      // Update the pending registration status
      const { error: updateError } = await supabase
        .from('pending_registrations')
        .update({ status: 'approved' })
        .eq('id', registration.id);
      
      if (updateError) throw updateError;
      
      // Refresh the data
      loadData();
    } catch (error: any) {
      console.error('Error approving registration:', error);
      setError(error.message || 'Failed to approve registration');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setProcessingId(id);
      setError(null);
      
      const { error } = await supabase
        .from('pending_registrations')
        .update({ status: 'rejected' })
        .eq('id', id);
      
      if (error) throw error;
      
      // Refresh the data
      loadData();
    } catch (error: any) {
      console.error('Error rejecting registration:', error);
      setError(error.message || 'Failed to reject registration');
    } finally {
      setProcessingId(null);
    }
  };

  const getDefaultHouseId = async (): Promise<string> => {
    // Get the first house from the database
    const { data, error } = await supabase
      .from('houses')
      .select('house_id')
      .limit(1)
      .single();
    
    if (error) {
      console.error('Error getting default house:', error);
      throw new Error('Failed to get default house');
    }
    
    return data.house_id;
  };

  // Filter registrations based on search term
  const filteredRegistrations = pendingRegistrations.filter(reg => 
    reg.status === 'pending' && (
      `${reg.first_name} ${reg.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reg.first_name_tamil && reg.last_name_tamil && 
        `${reg.first_name_tamil} ${reg.last_name_tamil}`.toLowerCase().includes(searchTerm.toLowerCase())) ||
      reg.department?.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Pagination
  const totalPages = Math.ceil(filteredRegistrations.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRegistrations = filteredRegistrations.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('pendingRegistrations.title')}</h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('pendingRegistrations.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('pendingRegistrations.noRegistrations.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? t('pendingRegistrations.noRegistrations.withFilters') : t('pendingRegistrations.noRegistrations.noFilters')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('pendingRegistrations.table.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('pendingRegistrations.table.department')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('pendingRegistrations.table.dateOfBirth')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('pendingRegistrations.table.gender')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('pendingRegistrations.table.competitions')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('pendingRegistrations.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRegistrations.map((registration) => (
                  <tr key={registration.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {registration.first_name} {registration.last_name}
                      </div>
                      {registration.first_name_tamil && registration.last_name_tamil && (
                        <div className="text-sm text-gray-500">
                          {registration.first_name_tamil} {registration.last_name_tamil}
                        </div>
                      )}
                      {registration.student_id && (
                        <div className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <User className="h-3 w-3 mr-1" />
                          Existing Participant
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {registration.department?.name}
                      </div>
                      {registration.department?.name_tamil && (
                        <div className="text-sm text-gray-500">
                          {registration.department.name_tamil}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {format(new Date(registration.date_of_birth), 'dd.MM.yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        registration.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                      }`}>
                        {registration.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <ul className="list-disc pl-5">
                          {registration.competitions.map((compId) => (
                            <li key={compId}>
                              {competitions[compId]?.event_name || compId}
                              {competitions[compId]?.category && ` - ${competitions[compId].category.category_name}`}
                              {competitions[compId]?.age_group && ` (${competitions[compId].age_group.group_name})`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApprove(registration)}
                          disabled={processingId === registration.id}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-green-300"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          {t('pendingRegistrations.approve')}
                        </button>
                        <button
                          onClick={() => handleReject(registration.id)}
                          disabled={processingId === registration.id}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300"
                        >
                          <X className="h-4 w-4 mr-1" />
                          {t('pendingRegistrations.reject')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
              >
                {t('pendingRegistrations.previous')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
              >
                {t('pendingRegistrations.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('pendingRegistrations.showing')} <span className="font-medium">{startIndex + 1}</span> {t('pendingRegistrations.to')}{' '}
                  <span className="font-medium">
                    {Math.min(startIndex + ITEMS_PER_PAGE, filteredRegistrations.length)}
                  </span>{' '}
                  {t('pendingRegistrations.of')} <span className="font-medium">{filteredRegistrations.length}</span> {t('pendingRegistrations.results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <span className="sr-only">{t('pendingRegistrations.previous')}</span>
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <span className="sr-only">{t('pendingRegistrations.next')}</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingRegistrationsPage;